<template>
    <div class="fp-wrap">
        <div class="foto-pessoa" :class="{ 'fp-editavel': editavel }" :style="estiloCaixa"
             :title="editavel ? 'Clique para enviar ou trocar a foto' : (nome || '')"
             @click="editavel && abrirSeletor()">
        <img v-if="src" :src="src" :alt="nome || 'Foto'" :style="estiloImg">

        <div v-else-if="carregando" class="fp-estado fp-carregando" :style="estiloFonte">
            <i class="el-icon-loading"></i>
        </div>

        <!--
            SEM FOTO é um estado real e precisa parecer com um: iniciais em fundo neutro.
            Nunca uma foto genérica de criança — isso seria dado falso na tela de quem
            precisa identificar o aluno.
        -->
        <div v-else class="fp-estado fp-sem-foto" :style="estiloFonte">
            <span v-if="iniciais">{{ iniciais }}</span>
            <i v-else class="el-icon-user"></i>
        </div>

        <!--
            MER 28 — "Permitir ao profissional responsável pela merenda escolar INSERIR uma
            foto do aluno". A auditoria integrada mostrou que a rota de envio existia sem
            nenhuma tela: o Secretário, que opera a merenda, não alcançava `aluno/uploadFoto`
            e não tinha outro caminho. O envio passa a acontecer aqui, na própria tela onde o
            aluno aparece.
        -->
        <div v-if="editavel" class="fp-overlay">
            <i :class="enviando ? 'el-icon-loading' : 'el-icon-camera'"></i>
        </div>
        </div>

        <input v-if="editavel" ref="arquivo" type="file" class="fp-input"
               accept="image/jpeg,image/png,image/webp" @change="enviar">

        <el-button v-if="editavel && src" type="text" class="fp-remover" @click="remover">
            remover
        </el-button>
    </div>
</template>

<script>
/**
 * Foto de uma pessoa (aluno ou servidor), buscada pela rota AUTENTICADA.
 *
 * ==================== POR QUE ESTE COMPONENTE EXISTE ====================
 *
 * A foto vive em disco privado e NÃO é alcançável por URL. Montar
 * `${baseUrlApi}/storage/${foto_path}` — como as telas faziam — deixou de funcionar e, mais
 * importante, nunca deveria ter funcionado: era imagem de criança servida sem autenticação.
 *
 * Aqui a imagem é buscada com o token, chega como blob e vira uma URL de objeto local, que é
 * revogada quando o componente morre. O caminho do arquivo nunca chega ao navegador.
 *
 * Nenhuma tela deve buscar foto por conta própria. Se precisar de foto, use este componente.
 */
export default {
    name: 'FotoPessoa',

    props: {
        /** Rota de leitura, sem a base. Ex.: 'api/aluno-foto', 'api/merenda/aluno-foto'. */
        endpoint: { type: String, required: true },

        /** Id da pessoa. Null/0 = não busca. */
        id: { type: [Number, String], default: null },

        /** Nome, usado para as iniciais quando não há foto. */
        nome: { type: String, default: '' },

        tamanho: { type: Number, default: 48 },

        redonda: { type: Boolean, default: true },

        /** Muda para forçar recarga (após enviar/remover foto). */
        versao: { type: [Number, String], default: 0 },

        /**
         * Quando true, clicar na foto abre o seletor de arquivo. O envio vai para
         * `${endpointEscrita}-enviar/${id}` e a remoção para `${endpointEscrita}-remover/${id}`.
         *
         * O sufixo vem DEPOIS do recurso, não antes do id: as URIs de escrita não podem
         * conter a de leitura, senão o middleware (que casa por substring) concede a escrita
         * a quem só recebeu a leitura. Foi um achado real da auditoria integrada.
         *
         * A tela decide se OFERECE; o backend decide se ACEITA — o Service valida perfil,
         * escopo, MIME real, extensão e tamanho, e recusa mesmo que o botão apareça.
         */
        editavel: { type: Boolean, default: false }
    },

    data () {
        return { src: null, carregando: false, enviando: false }
    },

    computed: {
        iniciais () {
            const n = (this.nome || '').trim()
            if (!n) return ''

            const partes = n.split(/\s+/).filter(p => p.length > 2)
            const base = partes.length ? partes : n.split(/\s+/)

            return base.slice(0, 2).map(p => p.charAt(0).toUpperCase()).join('')
        },

        estiloCaixa () {
            return {
                width: this.tamanho + 'px',
                height: this.tamanho + 'px',
                borderRadius: this.redonda ? '50%' : '4px'
            }
        },

        estiloImg () {
            return { borderRadius: this.redonda ? '50%' : '4px' }
        },

        estiloFonte () {
            return { fontSize: Math.max(10, Math.round(this.tamanho / 2.8)) + 'px' }
        }
    },

    watch: {
        id: 'carregar',
        endpoint: 'carregar',
        versao: 'carregar'
    },

    mounted () { this.carregar() },

    beforeDestroy () { this.liberar() },

    methods: {
        liberar () {
            if (this.src) {
                window.URL.revokeObjectURL(this.src)
                this.src = null
            }
        },

        abrirSeletor () {
            if (this.enviando || !this.id) return
            this.$refs.arquivo.value = ''
            this.$refs.arquivo.click()
        },

        enviar (e) {
            const arquivo = e.target.files && e.target.files[0]

            if (!arquivo) return

            this.enviando = true

            const fd = new FormData()
            fd.append('arquivo', arquivo)

            this.$axios.post(`${this.$store.state.config.baseUrlApi}/${this.endpoint}-enviar/${this.id}`,
                             fd, this.$store.getters.getHeaderFileAuth)
                .then(r => {
                    this.enviando = false

                    // O backend recusa MIME falso, extensão perigosa e tamanho — mostre o motivo.
                    if (r.data && r.data.erro) {
                        return window.eventBus.$emit('alert', { msg: r.data.erro, type: 'warning' })
                    }

                    window.eventBus.$emit('alert', { msg: 'Foto enviada.', type: 'success' })
                    this.carregar()
                    this.$emit('alterada')
                })
                .catch(err => { this.enviando = false; this.$store.commit('throwException', err) })
        },

        remover () {
            this.$msgbox.confirm('Remover a foto deste aluno?', 'Atenção',
                { confirmButtonText: 'Sim', cancelButtonText: 'Não', type: 'warning' })
                .then(() => {
                    this.$axios.delete(`${this.$store.state.config.baseUrlApi}/${this.endpoint}-remover/${this.id}`,
                                       this.$store.getters.getHeaderAuth)
                        .then(r => {
                            if (r.data && r.data.erro) {
                                return window.eventBus.$emit('alert', { msg: r.data.erro, type: 'warning' })
                            }

                            window.eventBus.$emit('alert', { msg: 'Foto removida.', type: 'success' })
                            this.carregar()
                            this.$emit('alterada')
                        })
                        .catch(err => this.$store.commit('throwException', err))
                })
                .catch(() => {})
        },

        carregar () {
            this.liberar()

            if (!this.id) return

            this.carregando = true

            const cfg = Object.assign({}, this.$store.getters.getHeaderAuth, { responseType: 'blob' })

            this.$axios.get(`${this.$store.state.config.baseUrlApi}/${this.endpoint}/${this.id}`, cfg)
                .then(r => {
                    this.carregando = false

                    // 204 = a pessoa não tem foto. Estado normal, não erro.
                    if (!r.data || r.data.size === 0 || r.status === 204) return

                    /*
                     * Quando a permissão nega, o backend responde 200 com JSON de erro — o
                     * contrato legado do middleware. Como pedimos blob, esse JSON chegaria
                     * aqui como um "arquivo" e viraria uma imagem quebrada. Detecta e trata
                     * como ausência de foto.
                     */
                    if (r.data.type && r.data.type.indexOf('application/json') === 0) return

                    this.src = window.URL.createObjectURL(r.data)
                })
                .catch(() => { this.carregando = false })
        }
    }
}
</script>

<style scoped>
.foto-pessoa {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    background: #f0f2f5;
    border: 1px solid #dcdfe6;
    flex: none;
    vertical-align: middle;
}

.foto-pessoa img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
}

.fp-estado {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    color: #909399;
    font-weight: 600;
    letter-spacing: .5px;
    user-select: none;
}

.fp-sem-foto { background: #f0f2f5; }
.fp-carregando { background: #fafafa; }

.fp-wrap { display: inline-block; position: relative; text-align: center; }
.fp-editavel { cursor: pointer; }
.fp-input { display: none; }

.fp-overlay {
    position: absolute;
    left: 0; top: 0; right: 0; bottom: 0;
    display: flex; align-items: center; justify-content: center;
    background: rgba(0, 0, 0, .38);
    color: #fff; font-size: 14px;
    opacity: 0; transition: opacity .15s;
    border-radius: inherit;
    pointer-events: none;
}

.fp-editavel:hover .fp-overlay { opacity: 1; }

.fp-remover { padding: 0; font-size: 11px; line-height: 1; margin-top: 2px; }
</style>
